import axios from "axios";
import URL from "../../config/envUrls";
import Auth from "../../config/auth";
import constants from "../../constants/constants";
import {
  createRequestKey,
  getPendingRequest,
  registerRequest,
  unregisterRequest
} from "./requestTracker";

const {
  API_TIME_OUT,
  HTTP_REQ_DEFAULT_HEADERS,
  HTTP_STATUS: { UNAUTHORISED, FORBIDDEN },
  PAGE_ROUTES: { LOGIN_ROUTE, NOT_AUTH_ROUTE }
} = constants;

// Step-1: Create a new Axios instance with a custom config.
// The timeout is set to 5min. If the request takes longer than
// that then the request will be aborted.
const customAxios = axios.create({
  baseURL: URL.API.baseURL,
  timeout: API_TIME_OUT,
  headers: {
    sessionKey: Auth.getSessionKey(),
    post: {
      Accept: HTTP_REQ_DEFAULT_HEADERS.Accept,
      "Content-Type": HTTP_REQ_DEFAULT_HEADERS["Content-Type"]
    }
  }
});

// Step-2: Create request, response & error handlers
const requestHandler = request => {
  // Token will be dynamic so we can use any app-specific way to always
  // fetch the new token before making the call
  request.headers.sessionKey = Auth.getSessionKey();
  return request;
};

const responseHandler = response => {
  const { status } = response;
  if (status === UNAUTHORISED || status === FORBIDDEN) {
    window.location.replace(LOGIN_ROUTE);
  }
  return response;
};

const errorHandler = (error = "") => {
  const { response = "" } = error || "";
  const { status } = response;
  if (
    status === UNAUTHORISED ||
    status === FORBIDDEN ||
    response?.data?.ResponseCode === "SessionExpired" ||
    response?.data?.ErrorMessage?.ResponseCode === "SessionExpired"
  ) {
    window.location.replace(NOT_AUTH_ROUTE);
  }
  return Promise.reject(error);
};

// Step-3: Configure/make use of request & response interceptors from Axios
// Note: You can create one method say configureInterceptors, add below in that,
// export and call it in an init function of the application/page.
customAxios.interceptors.request.use(
  request => requestHandler(request),
  error => errorHandler(error)
);

customAxios.interceptors.response.use(
  response => responseHandler(response),
  error => errorHandler(error)
);

// Step-4: Wrap the axios instance to add request deduplication
// This returns cached promises for duplicate in-flight requests
const originalRequest = customAxios.request.bind(customAxios);

const deduplicatedAxios = (config) => {
  // Skip deduplication for non-GET/POST data fetching (e.g., PUT, DELETE, file uploads)
  // Also skip if explicitly disabled via config
  const shouldDedupe = config.deduplicate !== false;

  if (!shouldDedupe) {
    return originalRequest(config);
  }

  const requestKey = createRequestKey(config);

  // Check if same request is already in-flight
  const pendingPromise = getPendingRequest(requestKey);
  if (pendingPromise) {
    // if (process.env.NODE_ENV === "development") {
    //   console.log(`[Request Dedup] Returning cached promise for: ${config.url}`);
    // }
    return pendingPromise;
  }

  // Make the actual request and cache the promise
  const requestPromise = originalRequest(config)
    .then((response) => {
      unregisterRequest(requestKey);
      return response;
    })
    .catch((error) => {
      unregisterRequest(requestKey);
      throw error;
    });

  registerRequest(requestKey, requestPromise);

  return requestPromise;
};

// Override axios methods to use deduplication
const wrappedAxios = Object.assign(
  (config) => deduplicatedAxios(config),
  customAxios,
  {
    request: deduplicatedAxios,
    get: (url, config) => deduplicatedAxios({ ...config, method: "get", url }),
    post: (url, data, config) => deduplicatedAxios({ ...config, method: "post", url, data }),
    put: (url, data, config) => deduplicatedAxios({ ...config, method: "put", url, data, deduplicate: false }),
    patch: (url, data, config) => deduplicatedAxios({ ...config, method: "patch", url, data, deduplicate: false }),
    delete: (url, config) => deduplicatedAxios({ ...config, method: "delete", url, deduplicate: false })
  }
);

// Step-5: Export the wrapped Axios instance with deduplication
export default wrappedAxios;
